import React from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl-next';

import { Flex, Text, Inline, xcss, Stack, Box } from '@atlaskit/primitives';
import Button, { IconButton } from '@atlaskit/button/new';
import EmojiFrequentIcon from '@atlaskit/icon/glyph/emoji/frequent';
import Heading from '@atlaskit/heading';
import CloseIcon from '@atlaskit/icon/core/close';
import CopyIcon from '@atlaskit/icon/glyph/copy';

import { SidePanel, SidePanelHeader, SidePanelBody, SidePanelFooter } from '@confluence/side-panel';
import { useSidePanelStateActions } from '@confluence/side-panel-state-container';

const headerContainerStyles = xcss({
	justifyContent: 'space-between',
	alignItems: 'center',
});

const headerTitleStyles = xcss({
	alignItems: 'center',
});

const contentBodyTitleStyles = xcss({
	fontSize: '14px',
	fontWeight: '600',
});

const i18n = defineMessages({
	versionHistoryTitle: {
		id: 'version-history.title',
		defaultMessage: 'Version History',
		description: 'Title for version history component',
	},
	versionHistoryIconLabel: {
		id: 'version-history.icon.label',
		defaultMessage: 'Frequent clock icon',
		description: 'aria-label for frequent clock icon',
	},
	versionHistoryCloseButtonLabel: {
		id: 'version-history.close.button.icon.label',
		defaultMessage: 'Close button',
		description: 'aria-label for close button',
	},
	currentVersionTitle: {
		id: 'version-history.current.version.title',
		defaultMessage: 'Current version',
		description: 'Title for current version section',
	},
	autoSavedVersionsTitle: {
		id: 'version-history.auto-saved.version.title',
		defaultMessage: 'Autosaved versions',
		description: 'Title for current version section',
	},
	restoreWhiteboardLabel: {
		id: 'version-history.restore.whiteboard.label',
		defaultMessage: 'Restore as new whiteboard',
		description: 'aria label for restore as new whiteboard button',
	},
	restoreWhiteboardButtonText: {
		id: 'version-history.restore.whiteboard.button.text',
		defaultMessage: 'Restore as new whiteboard',
		description: 'Button text to restore whiteboard',
	},
	copyIconLabel: {
		id: 'version-history.copy.icon.label',
		defaultMessage: 'Copy icon',
		description: 'aria-label for copy icon',
	},
});

type WhiteboardVersionHistoryProps = {
	headerHeight?: number;
};

export const WhiteboardVersionHistory = ({ headerHeight = 0 }: WhiteboardVersionHistoryProps) => {
	const { hideSidePanel } = useSidePanelStateActions();
	const { formatMessage } = useIntl();

	const renderVersionHistoryItem = (item: string) => {
		return (
			<Box paddingBlock="space.100" paddingInline="space.200">
				<Flex direction="row" alignItems="center" justifyContent="space-between">
					<Text size="medium">{item}</Text>
					<IconButton
						appearance="subtle"
						icon={CopyIcon}
						label={formatMessage(i18n.copyIconLabel)}
					/>
				</Flex>
			</Box>
		);
	};

	return (
		<SidePanel headerHeight={headerHeight}>
			<SidePanelHeader>
				<Flex direction="row" xcss={headerContainerStyles}>
					<Inline space="space.100" xcss={headerTitleStyles}>
						<EmojiFrequentIcon label={formatMessage(i18n.versionHistoryIconLabel)} />
						<Heading size="small" as="h5">
							<FormattedMessage {...i18n.versionHistoryTitle} />
						</Heading>
					</Inline>
					<IconButton
						appearance="subtle"
						icon={CloseIcon}
						onClick={hideSidePanel}
						label={formatMessage(i18n.versionHistoryCloseButtonLabel)}
					/>
				</Flex>
			</SidePanelHeader>
			<SidePanelBody>
				<Stack>
					<Box xcss={contentBodyTitleStyles} paddingBlock="space.100" paddingInline="space.200">
						<FormattedMessage {...i18n.currentVersionTitle} />
					</Box>
					{renderVersionHistoryItem('Aug 9th, 11:00 pm')}
				</Stack>
				<Stack>
					<Box xcss={contentBodyTitleStyles} paddingBlock="space.100" paddingInline="space.200">
						<FormattedMessage {...i18n.autoSavedVersionsTitle} />
					</Box>
					{renderVersionHistoryItem('Apr 12, 12:00 am')}
				</Stack>
			</SidePanelBody>
			<SidePanelFooter>
				<Button
					appearance="primary"
					aria-label={formatMessage(i18n.restoreWhiteboardLabel)}
					shouldFitContainer
				>
					<FormattedMessage {...i18n.restoreWhiteboardButtonText} />
				</Button>
			</SidePanelFooter>
		</SidePanel>
	);
};
